import { type BreadcrumbListSchema } from '@cx-web/api-clients/template-api/types';
import { escapeJsonForInlineScript, getRepubMetaFromRepub, removeRepubPrefix } from '@cx-web/utils';

import { type Repub } from '@cx-modules/constants';

import HomeIcon from '../../primitives/icon/icons/HomeIcon';

import styles from './Breadcrumbs.module.css';

export interface BreadcrumbsProps {
  /** JSON-LD BreadcrumbList from the page template; derived from the path when omitted. */
  dataSchema?: BreadcrumbListSchema;
  /** UK footer restyle (OCGC-3445): when true the home crumb uses the `HomeIcon` glyph; set only by the footer. */
  isUkFooterStyle?: boolean;
  /** Full request path including the repub segment, e.g. `/us/terms-and-conditions`. */
  path: string;
  /** Current repub (e.g. `US`) — drives the link prefix. */
  repub: Repub;
}

/** The crumbs a path yields once its repub prefix is stripped — `[]` on a repub home (`/us/`, `/`). */
export const getBreadcrumbSegments = (path: string, repub: Repub): string[] =>
  removeRepubPrefix(path, repub).split('/').filter(Boolean);

// OCGC-3445: single source of truth for "is the footer showing the breadcrumbs?", called by both the
// footer and each app's page-body gate so they can't disagree. Do not inline. Rationale (incl. the
// repub-home segment check) in doc/architecture/OCGC-3445-footer-breadcrumbs.md.
export const footerRendersBreadcrumbs = ({
  isUkFooterStyle,
  path,
  repub,
}: {
  isUkFooterStyle?: boolean;
  path?: string;
  repub?: Repub;
}): boolean => isUkFooterStyle === true && !!path && !!repub && getBreadcrumbSegments(path, repub).length > 0;

// Shared page-template BREADCRUMBS component (RR7 atlas + atlas-astro), pure presentational. Under
// `isUkFooterStyle` (OCGC-3445) it renders inside the CxFooter island instead of the page body.
export const Breadcrumbs = ({ dataSchema, isUkFooterStyle, path, repub }: BreadcrumbsProps): React.JSX.Element => {
  // `repubPath` is the URL prefix for the repub (`/us`, or `''` for OC); strip it to get the segments.
  const { repubPath: urlRepub } = getRepubMetaFromRepub(repub);
  const segments = getBreadcrumbSegments(path, repub);

  // Absolute repub home (`/us/`, or `/` for OC). Preferred over a relative `./` href, which resolves
  // against the current path and breaks for nested breadcrumbs (it lands on the parent dir, not home).
  const homeHref = `${urlRepub}/`;

  // Prefer the template's JSON-LD; otherwise derive a structurally-faithful one from the path segments.
  const jsonLd: BreadcrumbListSchema = dataSchema ?? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: segments.map((segment, idx) => ({
      '@type': 'ListItem',
      item: {
        '@id': `${urlRepub}/${segments.slice(0, idx + 1).join('/')}`,
        '@type': 'WebPage',
        name: segment.replace(/-/g, ' '),
      },
      position: idx + 1,
    })),
  };

  return (
    <nav aria-label="breadcrumbs" className={styles.breadcrumbsContainer} data-testid="breadcrumbs">
      <div className={styles.breadcrumbsWrapper}>
        <ol className={styles.breadcrumbsOl}>
          <li className={styles.breadCrumbLi}>
            <a data-testid="home-anchor" href={homeHref}>
              {isUkFooterStyle ? (
                <HomeIcon aria-label="home" className={styles.homeIcon} data-testid="home-image-uk" role="img" />
              ) : (
                <svg
                  aria-label="home"
                  data-testid="home-image"
                  fill="none"
                  height="14"
                  role="img"
                  viewBox="0 0 16 16"
                  width="14"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="m15.568 6.96-.001-.002L9.04.432A1.463 1.463 0 0 0 8 0c-.394 0-.764.153-1.042.432L.434 6.955l-.007.006a1.475 1.475 0 0 0 1.026 2.512h.26v4.803c0 .95.773 1.724 1.724 1.724H5.99c.26 0 .47-.21.47-.469v-3.765c0-.434.352-.787.786-.787h1.506c.434 0 .786.353.786.787v3.765c0 .26.21.469.47.469h2.553c.95 0 1.724-.773 1.724-1.724V9.473h.24c.394 0 .764-.153 1.042-.431a1.475 1.475 0 0 0 .001-2.083z"
                    fill="#D6D6D6"
                  />
                </svg>
              )}
            </a>
          </li>
          {segments.map((segment, idx) => {
            const href = `${urlRepub}/${segments.slice(0, idx + 1).join('/')}`;
            const isLast = idx === segments.length - 1;
            const formatted = segment.replace(/-/g, ' ');

            const text = (
              <span className={styles.breadCrumbText} data-testid="breadcrumb-text">
                {formatted}
              </span>
            );

            return (
              <li className={styles.breadCrumbLi} key={href}>
                <span className={styles.arrowWrap}>
                  <svg
                    aria-label="arrow"
                    className={styles.arrow}
                    data-testid="arrow-image"
                    fill="none"
                    height="14"
                    role="img"
                    viewBox="0 0 9 16"
                    width="8"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M8 2 2 8l6 6" stroke="#fff" strokeWidth="2.304" />
                  </svg>
                </span>
                {isLast ? (
                  <span className={styles.breadCrumbLast}>{text}</span>
                ) : (
                  <a className={styles.breadCrumbLink} data-testid="breadcrumb-link" href={href} title={formatted}>
                    {text}
                  </a>
                )}
              </li>
            );
          })}
        </ol>
      </div>
      <script dangerouslySetInnerHTML={{ __html: escapeJsonForInlineScript(jsonLd) }} type="application/ld+json" />
    </nav>
  );
};
